import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { object, string } from "yup";
import { UserFormValues } from "src/models/User.model";
import UserForm from "../Layouts/UserForm";
import {
  useGetAdminDataQuery,
  useUpdateUserMutation,
} from "src/services/UserService";
import { showToast } from "src/utils/showToaster";
import { CircularProgress } from "@mui/material";

type Props = {
  onClose: () => void;
  userId: string;
};

const EditUserFormWrapper = ({ onClose, userId }: Props) => {
  const [items, setItems] = useState<any>({});

  const { isLoading, isFetching, data } = useGetAdminDataQuery({ id: userId });
  const [updateUser] = useUpdateUserMutation();

  useEffect(() => {
    if (!isFetching && !isLoading) {
      setItems(data?.data);
    }
  }, [data, isFetching, isLoading]);

  // Form Initial Values
  const initialValues: UserFormValues = {
    firstName: items?.firstName || "",
    lastName: items?.lastName || "",
    email: items?.email || "",
    password: "",
    mobile: items?.mobile || "",
    altMobile: items?.altMobile || "",
    dob: items?.dob || "",
    gender: { label: items?.gender, value: items?.gender },
    upiId: items?.upiId || "",
    subjects: items?.subjects || "",
    walletAmount: items?.walletAmount || "",
    course: items?.course || "",
    level: items?.level || "",
    referralCode: items?.referralCode || "",
    referralBy: items?.referralBy || "",
    bankAccountDetails: items?.bankAccountDetails?.map(
      (accountDetails: any) => ({
        accountHolderName: accountDetails.accountHolderName || "",
        bankAccountNumber: accountDetails.bankAccountNumber || "",
        ifscCode: accountDetails.ifscCode || "",
        bankName: accountDetails.bankName || "",
        branchName: accountDetails.branchName || "",
      })
    ),
    address: items?.address?.map((address: any) => ({
      country: address.country || "",
      state: address.state || "",
      city: address.city || "",
      pincode: address.pincode || "",
      addressLine: address.addressLine || "",
      landmark: address.landmark || "",
      addressType: address.addressType || "",
    })),
  };

  // Validation Schema
  const validationSchema = object().shape({
    // email: string().email().required("Please enter email"),
    // password: string().required("Please enter password"),
    mobile: string().matches(/^[0-9]{10}$/, {
      message: "Mobile number should contain exactly 10 digits.",
    }),
    //   .required("Please enter mobile number"),
    // altMobile: string().matches(/^[0-9]{10}$/, {
    //   message: "Mobile number should contain exactly 10 digits.",
    // }),
    // state: string().required("Please enter state"),
    // city: string().required("Please enter city"),
    // pincode: string().required("Please enter pincode"),
    // addressLine: string().required("Please enter address"),
    // accountHolderName: string().required("Please enter account holder name"),
    // bankAccountNumber: string()
    //   .trim()
    //   .matches(/^\d{12,18}$/, {
    //     message: "Bank account number must be 12 to 18 digits",
    //   })
    //   .required("Please enter bank account number"),
    // ifscCode: string()
    //   .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, {
    //     message: "Invalid IFSC code",
    //   })
    //   .required("Please enter IFSC code"),
    // bankName: string().required("Please enter bank name"),
    // branchName: string().required("Please enter branch name"),
  });

  // Handle Submit
  const handleSubmit = (
    values: UserFormValues,
    { setSubmitting, resetForm }: FormikHelpers<UserFormValues>
  ) => {
    const payload = {
      firstName: items?.firstName || "",
      lastName: items?.lastName || "",
      mobile: values?.mobile,
      email: values?.email,
      altMobile: values?.altMobile,
      dob: values?.dob,
      gender: values.gender?.value,
      upiId: values?.upiId,
      subjects: values?.subjects.split(",").map((s) => s.trim()),
      walletAmount: values?.walletAmount,
      course: values?.course,
      level: values?.level,
      referralCode: values?.referralCode,
      referralBy: values?.referralBy,
      bankAccountDetails: values.bankAccountDetails.map(
        (bankAccountDetails): any => ({
          accountHolderName: bankAccountDetails.accountHolderName,
          bankAccountNumber: bankAccountDetails.bankAccountNumber,
          ifscCode: bankAccountDetails.ifscCode,
          bankName: bankAccountDetails.bankName,
          branchName: bankAccountDetails.branchName,
        })
      ),
      address: values.address.map((address): any => ({
        country: address.country,
        state: address.state,
        city: address.city,
        pincode: address.pincode,
        addressLine: address.addressLine,
        landmark: address.landmark,
        addressType: address.addressType,
      })),
    };

    updateUser({ id: userId, body: payload }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
          onClose();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <UserForm
              formikProps={formikProps}
              onClose={onClose}
              formType="EDIT"
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditUserFormWrapper;
